// The PDF renderer's way OUT of a render session (security review of #2897, L1).
//
// A render route signs in as the operator who pressed Send (useRenderSession:
// magic-link hash → verifyOtp). It cannot sign out on its own: a sign-out in
// the hook's effect cleanup destroys the session under React StrictMode's
// double mount, and the page never becomes ready. So the session outlived the
// render — the browser was closed, but its refresh token stayed valid on the
// server. For a SAAS_INVOICE that is a platform-mode admin session.
//
// Instead the page leaves a door on `window`, and the renderer (api/render-pdf.ts,
// revokeRenderSession) calls it AFTER page.pdf() and before closing the browser.
//
// ⚠️ scope: 'local' — ALWAYS. The default is 'global', which revokes EVERY
// session of that user: the operator would be signed out of every device,
// their own open browser included, each time they press Send. 'local' revokes
// this session's refresh token on the server and nothing else.
//
// No imports on purpose: the name is the contract with api/render-pdf.ts, and a
// test reads both files to keep them equal.

/** The global the renderer looks for. api/render-pdf.ts carries the same string. */
export const RENDER_SIGN_OUT_HOOK = '__kf49RenderSignOut'

interface SignOutClient {
  auth: { signOut(options: { scope: 'local' }): Promise<{ error: unknown }> }
}

/** Expose the render session's sign-out to the renderer. Resolves when the
 *  server has revoked this session; rejects (with no detail) when it refused. */
export function exposeRenderSignOut(client: SignOutClient): void {
  if (typeof window === 'undefined') return
  ;(window as unknown as Record<string, unknown>)[RENDER_SIGN_OUT_HOOK] = async (): Promise<'revoked'> => {
    const { error } = await client.auth.signOut({ scope: 'local' })
    if (error) throw new Error('render session sign-out refused')
    return 'revoked'
  }
}
